import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { InputLabel } from '@wajeez-web/components/ui';
import {
  PredefinedComponentFormType,
  SelectFormType,
  SelectOption,
} from '../Types';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import Select, {
  components,
  DropdownIndicatorProps,
  IndicatorSeparatorProps,
  OnChangeValue,
} from 'react-select';
import { SelectorIcon } from '@heroicons/react/outline';
import { useLayoutEffect } from 'react';

export interface FormSelectInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldType: SelectFormType;
  disabled?: boolean;
  showSelectIndicator?: boolean;
  showErrors?: boolean;
  errorMessages?: {
    required?: string;
  };
  widthClassName?: string;
  isClearable?: boolean;
}

const FormSelectInput = <TFormFields extends FieldValues>({
  fieldType,
  disabled,
  showErrors,
  errorMessages,
  widthClassName,
  isClearable,
  ...props
}: FormSelectInputProps<TFormFields>) => {
  const { field, fieldState } = useController({
    ...props,
    rules: {
      required: fieldType.required ? 'Required' : undefined,
      ...props.rules,
    },
  });

  const { dir } = useCommonSelector((state) => state.common.settings);

  const { t } = useTranslation();

  switch (fieldState.error?.type) {
    case 'required':
      fieldState.error.message = errorMessages?.required;

      break;
    default:
      break;
  }

  const isError = fieldState.error;

  const items =
    fieldType.fieldOptions.selectOptions?.map((item) => ({
      label: t(`selects.labels.${item.label}`, item.label),
      value: item.value,
    })) || [];

  useLayoutEffect(() => {
    const style = document.createElement('style');

    style.innerHTML = `
    .css-1xc3v61-indicatorContainer, .css-15lsz6c-indicatorContainer {
      cursor: pointer !important;
    }
    `;

    document.documentElement.appendChild(style);
  }, []);

  return (
    <div className={`${widthClassName}`}>
      {fieldType.label && (
        <InputLabel
          text={
            t(
              `inputs.labels.${fieldType.label}`,
              fieldType.label.toString() || ''
            ) as string
          }
          name={field.name}
          required={fieldType.required}
        />
      )}
      <Select
        isDisabled={disabled}
        // inputValue={inputValue}
        // isSearchable={onSearch ? true : false}
        // onInputChange={(val) => onSearch && onSearch(val)}
        placeholder={t(
          `inputs.placeholders.${fieldType.placeHolder}`,
          ''
        ).toString()}
        // name={name}
        // menuIsOpen={isMenuOpen}
        // defaultMenuIsOpen={isMenuOpen}
        isMulti={fieldType.fieldOptions.isMulti || undefined}
        options={items}
        value={items.find((s) => s.value === field.value)}
        onChange={(value) => {
          if (Array.isArray(value)) {
            field.onChange(value.map((v) => v.value));
          } else {
            field.onChange((value as any)?.value);
          }
          field.onBlur();
        }}
        className={'w-full input'}
        styles={{
          control: (base, state) => ({
            ...base,
            borderColor: isError ? 'var(--theme-error)' : base.borderColor,
            minHeight: '36px !important',
            // height: "36px !important",
            padding: '0px !important',
            textAlign: dir === 'ltr' ? 'left' : 'right',
            ' --tw-ring-opacity': 1,
            '--tw-ring-opacity': state.isFocused ? 0.1 : 'default',
            ' --tw-ring-color': state.isFocused
              ? 'var(--theme-ring-color)'
              : 'default',
            '--tw-ring-offset-shadow': state.isFocused
              ? 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)'
              : 'default',
            '--tw-ring-shadow': state.isFocused
              ? 'var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) var(--tw-ring-color)'
              : 'default',
            boxShadow: state.isFocused
              ? ' var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000)'
              : 'default',
          }),
          menu: (provided) => ({
            ...provided,
            position: 'absolute',
            'z-index': '999 !important',
            textAlign: dir === 'ltr' ? 'left' : 'right',
          }),
          container: (base) => ({
            ...base,
            fontSize: '14px !important',
            padding: '0px !important',
            height: 'auto !important',
          }),
        }}
        theme={(theme) => ({
          ...theme,
          zIndex: 999,
          // border: '0px none',
          width: '100%',
          borderRadius: 6,
          padding: 0,
          margin: 0,
          colors: {
            ...theme.colors,

            primary: 'var(--theme-primary)',
          },
        })}
        components={{ DropdownIndicator, IndicatorSeparator }}
        noOptionsMessage={() => (
          <div>
            {t(`selects.labels.${'No options'}`, 'No options').toString()}
          </div>
        )}
        isClearable={isClearable}
      />
      {isError && showErrors && (
        <div className="mt-2">
          <p className="mb-0 text-sm text-red-500">{`${
            (t(`inputs.messages.${fieldState.error?.message}`),
            fieldState.error?.message)
          }`}</p>
        </div>
      )}
    </div>
  );
};

const DropdownIndicator = (
  props: DropdownIndicatorProps<SelectOption, true>
) => {
  return (
    <components.DropdownIndicator {...props}>
      <SelectorIcon className="h-5 w-5 text-gray-400" />
    </components.DropdownIndicator>
  );
};

const indicatorSeparatorStyle = {
  alignSelf: 'stretch',
  backgroundColor: 'white',
  marginBottom: 8,
  marginTop: 8,
  width: 1,
};

const IndicatorSeparator = ({
  innerProps,
}: IndicatorSeparatorProps<SelectOption, true>) => {
  return <span style={indicatorSeparatorStyle} {...innerProps} />;
};

export default FormSelectInput;
